/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type Dispatch, type SetStateAction, useState, useEffect } from 'react';
import { createContext, useContext, type ReactNode } from 'react';
import { useRouter } from 'next/router';

const ReviewFiltersContext = createContext<ReviewFiltersInitialState | undefined>(undefined);

type ProviderProps = {
  children: ReactNode;
};

type ReviewFiltersInitialState = {
  selectedRatings: number[];
  setSelectedRatings: Dispatch<SetStateAction<number[]>>;
};

export const ReviewFiltersProvider: React.FC<ProviderProps> = ({ children }) => {
  const [selectedRatings, setSelectedRatings] = useState<number[]>([]);
  const router = useRouter();
  const { slug } = router.query;

  useEffect(() => {
    if (slug) {
      setSelectedRatings([]);
    }
  }, [slug]);

  return (
    <ReviewFiltersContext.Provider value={{ selectedRatings, setSelectedRatings }}>
      {children}
    </ReviewFiltersContext.Provider>
  );
};

export const useReviewFilters = () => {
  const context = useContext(ReviewFiltersContext);
  if (!context) {
    throw new Error('useReviewFilters must be used within the ReviewFiltersProvider');
  }
  return context;
};
